import { useNavigate } from 'react-router-dom'
import StatusBadge from './StatusBadge.jsx'

function formatRelativeTime(dateStr) {
  if (!dateStr) return '—'
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

const STATUS_BORDER = {
  ACTIVE: '#10b981',
  PAUSED: '#f59e0b',
  TERMINATED: '#ef4444',
}

export default function WorkspaceCard({ workspace, snapshotCount = 0 }) {
  const navigate = useNavigate()
  const borderColor = STATUS_BORDER[workspace.status] ?? '#22223a'

  return (
    <article
      role="button"
      tabIndex={0}
      aria-label={`Open workspace ${workspace.name}`}
      onClick={() => navigate(`/workspace/${workspace.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          navigate(`/workspace/${workspace.id}`)
        }
      }}
      className="relative cursor-pointer group rounded-lg overflow-hidden border border-border bg-surface transition-all duration-150 hover:border-border-bright hover:bg-surface2 animate-fade-in"
      style={{ borderLeft: `3px solid ${borderColor}` }}
    >
      {/* Subtle grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage:
            'radial-gradient(circle, #2e2e4a 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="relative p-5 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-text font-semibold text-sm leading-tight truncate group-hover:text-accent transition-colors">
              {workspace.name}
            </h3>
            {workspace.description && (
              <p className="text-text-dim text-xs mt-1 leading-relaxed line-clamp-2">
                {workspace.description}
              </p>
            )}
          </div>
          <StatusBadge status={workspace.status} size="xs" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-2xs text-text-faint tracking-widest uppercase font-mono">
              Agent
            </span>
            <span className="text-xs font-mono text-text-dim truncate">
              {workspace.agentId ?? '—'}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-2xs text-text-faint tracking-widest uppercase font-mono">
              Snapshots
            </span>
            <span className="text-sm font-mono text-accent font-semibold">
              {snapshotCount}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <span className="text-2xs font-mono text-text-faint truncate max-w-[60%]">
            {workspace.basePath}
          </span>
          <span className="text-2xs text-text-faint">
            {formatRelativeTime(workspace.updatedAt)}
          </span>
        </div>
      </div>
    </article>
  )
}
